import { type StoreApi } from "zustand/vanilla";
import { type VaultState } from "../model/vaultStore.ts";
import { type VaultTreeNode } from "../model/types.ts";
import { isHiddenFolderPath } from "../vault/attachments.ts";
import { CANVAS_SVG, DOC_SVG } from "./glyphs.ts";

interface Entry { path: string; name: string; dir: string; kind: VaultTreeNode["kind"]; needleName: string; needlePath: string }
interface Hit { entry: Entry; score: number }
interface SearchOptions { host: HTMLElement; onVisibility: (open: boolean) => void }
const MAX_HITS = 50;
const normalize = (s: string): string => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

/** Sidebar finder; query and results survive opening a document. */
export class SearchPalette {
  private input: HTMLInputElement;
  private list: HTMLElement;
  private status: HTMLElement;
  private entries: Entry[] = [];
  private hits: Hit[] = [];
  private cursor = 0;
  private renderToken = 0;
  private openToken = 0;

  constructor(private vault: StoreApi<VaultState>, private onOpen: (path: string, opts: { peek: boolean }) => void | Promise<void>, private opts: SearchOptions) {
    opts.host.innerHTML = `
      <div class="search-heading"><span>Search</span><button class="search-close" type="button" aria-label="Close search" title="Back to files">×</button></div>
      <div class="search-field">
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true"><circle cx="11" cy="11" r="6.6"/><path d="m16.2 16.2 4.3 4.3"/></svg>
        <input class="search-input" type="search" placeholder="Search files…" aria-label="Search files by name or path" aria-controls="search-results" aria-describedby="search-hint" spellcheck="false" autocomplete="off" />
      </div>
      <div class="search-status" role="status" aria-live="polite"></div>
      <div class="search-results" id="search-results" role="list" aria-label="Search results"></div>
      <div class="search-hint" id="search-hint">Enter: main view · ⇧Enter: side panel</div>`;
    this.input = opts.host.querySelector("input")!;
    this.list = opts.host.querySelector(".search-results")!;
    this.status = opts.host.querySelector(".search-status")!;
    opts.host.querySelector("button")!.addEventListener("click", () => this.close());
    opts.host.addEventListener("keydown", (e) => this.onKey(e));
    this.input.addEventListener("input", () => this.search(this.input.value));
    vault.subscribe((state, prev) => {
      if (state.adapter !== prev.adapter) this.reset();
      else if (state.tree !== prev.tree && this.isOpen) this.refresh();
    });
  }

  get isOpen(): boolean { return !this.opts.host.hidden; }
  toggle(): void { if (this.isOpen) this.close(); else this.open(); }
  open(): void {
    if (!this.vault.getState().adapter) return;
    this.opts.onVisibility(true);
    this.refresh();
    this.input.focus();
  }
  close(): void {
    this.opts.host.hidden = true;
    this.opts.onVisibility(false);
    document.getElementById("btn-search")?.focus();
  }
  reset(): void {
    this.renderToken++; this.openToken++;
    this.input.value = "";
    this.list.replaceChildren();
    this.status.textContent = "";
    this.entries = []; this.hits = [];
    this.opts.host.hidden = true;
  }
  private refresh(): void {
    const scroll = this.list.scrollTop;
    const selected = this.hits[this.cursor]?.entry.path;
    this.entries = [];
    this.collect(this.vault.getState().tree);
    this.search(this.input.value);
    const next = this.hits.findIndex((hit) => hit.entry.path === selected);
    if (next >= 0) { this.cursor = next; this.paintCursor(); }
    this.list.scrollTop = scroll;
  }
  private collect(nodes: VaultTreeNode[]): void {
    for (const node of nodes) {
      if (node.children) {
        if (!isHiddenFolderPath(node.path)) this.collect(node.children);
        continue;
      }
      const cut = node.path.lastIndexOf("/");
      this.entries.push({ path: node.path, name: node.name, dir: cut < 0 ? "" : node.path.slice(0, cut), kind: node.kind,
        needleName: normalize(node.name), needlePath: normalize(node.path) });
    }
  }
  private onKey(e: KeyboardEvent): void {
    // Canvas shortcuts must not handle keys typed in the sidebar.
    e.stopPropagation();
    if (e.key === "Escape") { e.preventDefault(); this.close(); return; }
    if (e.target !== this.input || e.isComposing) return;
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (!this.hits.length) return;
      this.cursor = (this.cursor + (e.key === "ArrowDown" ? 1 : -1) + this.hits.length) % this.hits.length;
      this.paintCursor();
      (this.list.children[this.cursor] as HTMLElement)?.scrollIntoView({ block: "nearest" });
      this.status.textContent = this.hits[this.cursor]!.entry.name;
    } else if (e.key === "Enter") {
      e.preventDefault();
      const path = this.hits[this.cursor]?.entry.path;
      // ⇧ lo abre al costado, sin soltar lo que hay en grande.
      if (path) void this.choose(path, e.shiftKey);
    }
  }
  private async choose(path: string, peek = false): Promise<void> {
    const token = ++this.openToken;
    this.status.textContent = "Opening…";
    try {
      await this.onOpen(path, { peek });
      if (token === this.openToken) {
        const where = peek ? "In the side panel" : "In main view";
        this.status.textContent = `${where}: ${path.split("/").pop()}`;
      }
    } catch (err) {
      if (token === this.openToken) this.status.textContent = "This file couldn't be opened. Try again.";
      console.error("Search open failed", path, err);
    }
  }
  private rank(entry: Entry, q: string): Hit | null {
    const at = entry.needleName.indexOf(q);
    if (at === 0) return { entry, score: 0 };
    if (at > 0) return { entry, score: /[\s\-_./]/.test(entry.needleName[at - 1]!) ? 1 : 2 };
    if (entry.needlePath.includes(q)) return { entry, score: 3 };
    return null;
  }
  private search(raw: string): void {
    const q = normalize(raw.trim());
    const all = q ? this.entries.map((entry) => this.rank(entry, q)).filter((hit): hit is Hit => hit !== null)
      .sort((a, b) => a.score - b.score || a.entry.name.localeCompare(b.entry.name))
      : this.entries.map((entry) => ({ entry, score: 0 }));
    this.hits = all.slice(0, MAX_HITS);
    this.cursor = 0;
    this.status.textContent = all.length > MAX_HITS ? `First ${MAX_HITS} of ${all.length} files — refine your search` : `${all.length} file${all.length === 1 ? "" : "s"}`;
    this.paint();
  }
  private paint(): void {
    const token = ++this.renderToken;
    this.list.replaceChildren();
    if (!this.hits.length) {
      const empty = document.createElement("div");
      empty.className = "search-empty";
      empty.textContent = this.entries.length ? "No matching files." : "This vault has no files yet.";
      this.list.append(empty);
      return;
    }
    const snippets: { path: string; el: HTMLElement }[] = [];
    for (const [i, hit] of this.hits.entries()) {
      const row = document.createElement("div");
      row.className = "search-row";
      row.setAttribute("role", "listitem");
      const heading = document.createElement("div");
      heading.className = "search-row-heading";
      const icon = document.createElement("span");
      icon.className = "tw-icon";
      icon.setAttribute("aria-hidden", "true");
      icon.innerHTML = hit.entry.kind === "canvas" ? CANVAS_SVG : DOC_SVG;
      const name = document.createElement("span");
      name.className = "search-row-name";
      name.textContent = hit.entry.name;
      name.title = hit.entry.name;
      heading.append(icon, name);
      const dir = document.createElement("div");
      dir.className = "search-row-dir";
      dir.textContent = hit.entry.dir || this.vault.getState().name;
      dir.title = hit.entry.path;
      row.append(heading, dir);
      if (/\.md$/i.test(hit.entry.path)) {
        const snippet = document.createElement("p");
        snippet.className = "search-snippet";
        row.append(snippet);
        snippets.push({ path: hit.entry.path, el: snippet });
      }
      const actions = document.createElement("div");
      actions.className = "search-row-actions";
      const button = document.createElement("button");
      button.type = "button";
      button.textContent = "Main view";
      button.setAttribute("aria-label", `Open ${hit.entry.name} in the main view`);
      button.addEventListener("click", (e) =>
        void this.choose(hit.entry.path, e.metaKey || e.ctrlKey),
      );
      actions.append(button);
      row.append(actions);
      row.addEventListener("focusin", () => { this.cursor = i; this.paintCursor(); });
      this.list.append(row);
    }
    this.paintCursor();
    // Bound disk concurrency and stop obsolete searches before reading more files.
    const adapter = this.vault.getState().adapter;
    let next = 0;
    const worker = async (): Promise<void> => {
      while (token === this.renderToken && adapter) {
        const item = snippets[next++];
        if (!item) return;
        try {
          const text = await adapter.read(item.path);
          if (token !== this.renderToken) return;
          const plain = text.replace(/^---\r?\n[\s\S]*?\r?\n---\s*/, "").slice(0, 1200)
            .replace(/[#>*_`\[\]]/g, "").replace(/\s+/g, " ").trim();
          item.el.textContent = plain.length > 140 ? `${plain.slice(0, 140)}…` : plain || "Empty document";
        } catch {
          if (token === this.renderToken) item.el.textContent = "Preview unavailable";
        }
      }
    };
    for (let i = 0; i < Math.min(4, snippets.length); i++) void worker();
  }
  private paintCursor(): void {
    [...this.list.children].forEach((row, i) => row.classList.toggle("is-cursor", i === this.cursor));
  }
}
